import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SEARCH_DEBOUNCE_MS } from "@/core/constants";
import { getLoadErrorMessage, isAPIError } from "@/core/services";
import type { FilterValue } from "@/ds";
import { applyCargaFornecedorFilters } from "./carga-fornecedor.filters";
import type { CargaFornecedorDados } from "./carga-fornecedor.filters";
import type { PeriodRange } from "./periodo";
import { periodoToRange } from "./periodo";
import { listAlocacoesPorFornecedor, listAlocacoesPorObra } from "./recursos.gateway";
import {
  aplicarCenarioFornecedores,
  aplicarCenarioObrasFornecedor,
  SEM_MARCAS,
} from "./simulacao/simulacao.aplicar";
import type { MarcasSimulacao } from "./simulacao/simulacao.aplicar";
import type { CenarioSimulacao } from "./simulacao/simulacao.model";
import type { AgrupamentoCarga } from "./recursos.types";

/** Sem o privilégio `hub.equipe:visualizar` a API devolve 403. */
export const CARGA_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar a carga de trabalho dos fornecedores.";
export const CARGA_ERROR_MESSAGE = "Não foi possível carregar a carga de trabalho.";

const NO_FILTERS: FilterValue = {};

export type CargaStatus = "idle" | "loading" | "success" | "error" | "forbidden";

interface CargaState {
  status: CargaStatus;
  /** Dados carregados, sem os filtros do painel (as opções deles vêm daqui). */
  dados: CargaFornecedorDados | null;
  errorMessage: string | null;
}

function aplicarCenario(
  dados: CargaFornecedorDados | null,
  cenario: CenarioSimulacao | null,
): { dados: CargaFornecedorDados | null; marcas: MarcasSimulacao } {
  if (!dados || !cenario) return { dados, marcas: SEM_MARCAS };

  if (dados.agrupamento === "obra") {
    const simulado = aplicarCenarioObrasFornecedor(dados.obras, cenario);
    return { dados: { agrupamento: "obra", obras: simulado.dados }, marcas: simulado.marcas };
  }

  const simulado = aplicarCenarioFornecedores(dados.fornecedores, cenario);
  return {
    dados: { agrupamento: "fornecedor", fornecedores: simulado.dados },
    marcas: simulado.marcas,
  };
}

export interface UseCargaDeTrabalhoParams {
  agrupamento: AgrupamentoCarga;
  /** Período já validado pelo seletor (início não posterior ao fim). */
  periodo: PeriodRange;
  search: string;
  /** Critérios do painel de Filtros, aplicados no cliente sobre os dados carregados. */
  filters?: FilterValue;
  /** `false` enquanto a perspectiva em tela não é a de Fornecedor. */
  enabled?: boolean;
  /** Cenário do Modo Simulação (HU 2225), aplicado sobre as alocações sem nova consulta. */
  cenario?: CenarioSimulacao | null;
}

export function useCargaDeTrabalho({
  agrupamento,
  periodo,
  search,
  filters = NO_FILTERS,
  enabled = true,
  cenario = null,
}: UseCargaDeTrabalhoParams) {
  const [state, setState] = useState<CargaState>({
    status: enabled ? "loading" : "idle",
    dados: null,
    errorMessage: null,
  });
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);

  const term = search.trim();
  const { dataInicio, dataFim } = periodoToRange(periodo);

  useEffect(() => {
    if (!enabled) {
      previousTerm.current = null;
      setState({ status: "idle", dados: null, errorMessage: null });
      return;
    }

    // Debounce só na digitação; carga inicial, período, agrupamento e retry são imediatos.
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        const request = { dataInicio, dataFim, busca: term, signal: controller.signal };
        const load: Promise<CargaFornecedorDados> =
          agrupamento === "obra"
            ? listAlocacoesPorObra(request).then((obras) => ({ agrupamento: "obra", obras }))
            : listAlocacoesPorFornecedor(request).then((fornecedores) => ({
                agrupamento: "fornecedor",
                fornecedores,
              }));

        load
          .then((dados) => {
            if (cancelled) return;
            setState({ status: "success", dados, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;

            const isForbidden = isAPIError(error) && error.status === 403;
            setState({
              status: isForbidden ? "forbidden" : "error",
              dados: null,
              errorMessage: isForbidden
                ? CARGA_FORBIDDEN_MESSAGE
                : getLoadErrorMessage(error, CARGA_ERROR_MESSAGE),
            });
          });
      },
      shouldDebounce ? SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [enabled, agrupamento, dataInicio, dataFim, term, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  // O cenário da simulação entra antes dos filtros: o painel filtra o que a tela mostra.
  const simulado = useMemo(() => aplicarCenario(state.dados, cenario), [state.dados, cenario]);

  // Como em Profissionais, o Gantt é montado sobre os dados já filtrados pelo painel.
  const filtrados = useMemo<CargaFornecedorDados | null>(
    () => (simulado.dados ? applyCargaFornecedorFilters(simulado.dados, filters) : null),
    [simulado.dados, filters],
  );

  return {
    ...state,
    dados: simulado.dados,
    brutos: state.dados,
    marcas: simulado.marcas,
    filtrados,
    reload,
  };
}
